import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { Bundle, BundleEntry, Resource } from 'fhir/r4';
import { backendEndPointToken, upstreamServerEndPointToken } from '../app.config';
import { BehaviorSubject, Observable, Subscription, combineLatest, of, throwError } from 'rxjs';
import { catchError, filter as rxFilter, map, switchMap, tap } from 'rxjs/operators';

export interface TableRowModel {
    id: string;
    resourceType: string;
    display?: string;
    rawResource: Resource;
    [key: string]: any;
}

type Primitive = string | number | boolean;
export type TableFilters = Record<string, Primitive | Primitive[] | null | undefined>;

export interface TableFilteringHttpOptions {
    headers?: HttpHeaders | { [header: string]: string | string[] };
    params?: HttpParams | { [param: string]: string | number | boolean | readonly (string | number | boolean)[] };
    context?: HttpContext;
    withCredentials?: boolean;
}

export interface TableFilteringInit<TResource extends Resource = Resource, TRow extends TableRowModel = TableRowModel> {
    resourceType: string;
    defaultQuery: string;
    dataSource: MatTableDataSource<TRow>;
    pageSize?: number;
    baseUrl?: string;
    maxCacheRows?: number;
    transformEntry?: (resource: TResource, entry?: BundleEntry<TResource>) => TRow;
    httpOptions?: TableFilteringHttpOptions;
    enableLocalFiltering?: boolean;
    localRows?: TRow[];
    localFilterPredicate?: (row: TRow, filters: TableFilters) => boolean;
    disableCountQuery?: boolean;
}

@Injectable({ providedIn: 'root' })
export class TableFilteringService<TResource extends Resource = Resource, TRow extends TableRowModel = TableRowModel> {
    private readonly DEFAULT_PAGE_SIZE = 25;
    private readonly HARD_MAX_CACHE_LIMIT = 200;

    private config?: TableFilteringInit<TResource, TRow>;
    private dataSource?: MatTableDataSource<TRow>;

    private pageSize = this.DEFAULT_PAGE_SIZE;
    private maxCacheLimit = this.HARD_MAX_CACHE_LIMIT;

    private readonly pageCache = new Map<number, TRow[]>();
    private readonly pageToNextLink = new Map<number, string>();
    private cachedRowTotal = 0;
    private cacheLocked = false;

    private currentPageIndex = 0;
    private defaultQueryUrl = '';
    private currentQueryUrl = '';
    private hasInit = false;

    private readonly filtersSubject = new BehaviorSubject<TableFilters>({});
    readonly filters$ = this.filtersSubject.asObservable();

    private readonly querySubject = new BehaviorSubject<string>('');
    private readonly pageSubject = new BehaviorSubject<number>(0);

    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    readonly loading$ = this.loadingSubject.asObservable();

    private readonly errorSubject = new BehaviorSubject<string | null>(null);
    readonly error$ = this.errorSubject.asObservable();

    private readonly totalSubject = new BehaviorSubject<number | null>(null);
    readonly total$ = this.totalSubject.asObservable();

    // When server total is unknown we expose a progressive/displayed total
    // that starts at pageSize and grows as pages are fetched (and shrinks
    // to the actual fetched count when no further pages exist).
     readonly displayedTotalSubject = new BehaviorSubject<number>(0);
    readonly displayedTotal$ = this.displayedTotalSubject.asObservable();

    private readonly unknownTotalSubject = new BehaviorSubject<boolean>(false);
    readonly unknownTotal$ = this.unknownTotalSubject.asObservable();

    private pipelineSub?: Subscription;
    private readonly upstreamBase = inject(upstreamServerEndPointToken);
    private readonly backendBase = inject(backendEndPointToken);
    private localFilteringEnabled = false;
    private localRows: TRow[] = [];
    private localFilteredRows: TRow[] = [];
    private localFilterPredicate?: (row: TRow, filters: TableFilters) => boolean;
    private disableCountQuery = false;

    constructor(private readonly http: HttpClient) { }

    init(config: TableFilteringInit<TResource, TRow>): void {
        this.disconnectPipeline();
        this.config = config;
        this.dataSource = config.dataSource;
        this.pageSize = config.pageSize ?? this.DEFAULT_PAGE_SIZE;
        this.maxCacheLimit = config.maxCacheRows ?? this.HARD_MAX_CACHE_LIMIT;
        this.localFilteringEnabled = config.enableLocalFiltering === true;
        this.disableCountQuery = config.disableCountQuery === true;
        this.localFilterPredicate = config.localFilterPredicate;
        this.localRows = [];
        this.localFilteredRows = [];

        this.resetCache();
        // initialize progressive displayed total to one page
        this.displayedTotalSubject.next(this.pageSize);
        this.defaultQueryUrl = this.buildQueryUrl(config.defaultQuery);
        this.currentQueryUrl = this.defaultQueryUrl;
        this.currentPageIndex = 0;

        if (this.localFilteringEnabled && Array.isArray(config.localRows) && config.localRows.length) {
            this.setLocalRows(config.localRows, false);
        }

        this.filtersSubject.next({});
        this.pageSubject.next(0);
        this.querySubject.next(this.currentQueryUrl);

        if (this.shouldUseLocalFiltering()) {
            this.applyLocalFilteringAndPaginate({}, 0);
        } else {
            this.connectPipeline();
            this.fetchTotalCount(this.currentQueryUrl);
        }
        this.hasInit = true;
    }

    destroy(): void {
        this.disconnectPipeline();
        this.localRows = [];
        this.localFilteredRows = [];
        this.localFilteringEnabled = false;
    }

    search(filters: TableFilters | null | undefined): void {
        this.ensureInitialized();
        const normalized = filters ?? {};
        this.filtersSubject.next(normalized);

        if (this.shouldUseLocalFiltering()) {
            this.applyLocalFilteringAndPaginate(normalized, 0);
            return;
        }

        const hasFilters = this.hasActiveFilters(normalized);
        const target = hasFilters
            ? this.buildQueryUrl(this.defaultQueryUrl, normalized)
            : this.defaultQueryUrl;

        this.applyNewQuery(target);
    }

    changePage(pageIndex: number): void {
        this.ensureInitialized();
        if (pageIndex < 0 || pageIndex === this.currentPageIndex) {
            return;
        }

        if (this.shouldUseLocalFiltering()) {
            this.emitLocalPage(pageIndex);
            return;
        }

        this.currentPageIndex = pageIndex;
        this.pageSubject.next(pageIndex);
    }

    refreshCurrentPage(pageIndex?: number): void {
        this.ensureInitialized();
        const target = pageIndex ?? this.currentPageIndex;
        if (target < 0) {
            return;
        }

        if (this.shouldUseLocalFiltering()) {
            this.emitLocalPage(target);
            return;
        }
        this.invalidateCachedPagesFrom(target);
        this.pageSubject.next(target);
    }

    enableLocalFiltering(rows?: TRow[]): void {
        this.localFilteringEnabled = true;
        if (Array.isArray(rows)) {
            this.setLocalRows(rows, false);
        }
        if (this.shouldUseLocalFiltering()) {
            const filters = this.filtersSubject.getValue() || {};
            this.applyLocalFilteringAndPaginate(filters, 0);
        }
    }

    disableLocalFiltering(): void {
        if (!this.localFilteringEnabled) {
            return;
        }
        this.localFilteringEnabled = false;
        this.localRows = [];
        this.localFilteredRows = [];
        this.connectPipeline();
        this.fetchTotalCount(this.currentQueryUrl);
        this.applyNewQuery(this.currentQueryUrl);
    }

    setLocalRows(rows: TRow[], reapplyFilters = true): void {
        this.localRows = Array.isArray(rows) ? [...rows] : [];
        if (reapplyFilters && this.shouldUseLocalFiltering()) {
            const filters = this.filtersSubject.getValue() || {};
            this.applyLocalFilteringAndPaginate(filters, 0);
        }
    }

    private connectPipeline(): void {
        this.disconnectPipeline();
        this.pipelineSub = combineLatest([this.querySubject, this.pageSubject])
            .pipe(
                rxFilter(([query]) => !!query),
                tap(() => this.loadingSubject.next(true)),
                switchMap(([query, pageIndex]) =>
                    this.resolvePage(query, pageIndex).pipe(
                        tap(() => this.errorSubject.next(null)),
                        catchError((error: Error) => {
                            this.errorSubject.next(error.message || 'Unable to load data');
                            return of<TRow[]>([]);
                        })
                    )
                ),
                tap(() => this.loadingSubject.next(false))
            )
            .subscribe((rows) => {
                if (!this.dataSource) {
                    return;
                }
                this.dataSource.data = rows;
            });
    }

    private disconnectPipeline(): void {
        this.pipelineSub?.unsubscribe();
        this.pipelineSub = undefined;
    }

    private applyNewQuery(queryUrl: string): void {
        this.querySubject.next('');
        this.currentQueryUrl = queryUrl;
        this.resetCache();
        this.currentPageIndex = 0;
        this.pageSubject.next(0);
        this.querySubject.next(queryUrl);
        this.fetchTotalCount(queryUrl);
    }

    private shouldUseLocalFiltering(): boolean {
        return this.localFilteringEnabled && this.localRows.length > 0;
    }

    private applyLocalFilteringAndPaginate(filters: TableFilters, pageIndex: number): void {
        const filtered = this.filterLocalRows(filters);
        this.localFilteredRows = filtered;
        const boundedIndex = Math.max(0, Math.min(pageIndex, this.calculateLocalMaxPage(filtered.length)));
        this.currentPageIndex = boundedIndex;
        const pageRows = this.extractLocalPage(boundedIndex);
        this.loadingSubject.next(false);
        this.errorSubject.next(null);
        this.unknownTotalSubject.next(false);
        this.totalSubject.next(filtered.length);
        if (this.dataSource) {
            this.dataSource.data = pageRows;
        }
    }

    private emitLocalPage(pageIndex: number): void {
        if (!this.localFilteredRows.length) {
            this.applyLocalFilteringAndPaginate(this.filtersSubject.getValue() || {}, pageIndex);
            return;
        }
        const boundedIndex = Math.max(0, Math.min(pageIndex, this.calculateLocalMaxPage(this.localFilteredRows.length)));
        this.currentPageIndex = boundedIndex;
        if (this.dataSource) {
            this.dataSource.data = this.extractLocalPage(boundedIndex);
        }
    }

    private calculateLocalMaxPage(totalCount: number): number {
        if (totalCount <= 0) {
            return 0;
        }
        return Math.max(0, Math.ceil(totalCount / this.pageSize) - 1);
    }

    private extractLocalPage(pageIndex: number): TRow[] {
        const start = pageIndex * this.pageSize;
        return this.localFilteredRows.slice(start, start + this.pageSize);
    }

    private filterLocalRows(filters: TableFilters): TRow[] {
        if (!this.localRows.length) {
            return [];
        }
        const predicate = this.localFilterPredicate ?? ((row: TRow, fs: TableFilters) => this.defaultLocalFilterPredicate(row, fs));
        const activeFilters = filters ?? {};
        return this.localRows.filter((row) => predicate(row, activeFilters));
    }

    private defaultLocalFilterPredicate(row: TRow, filters: TableFilters): boolean {
        const entries = Object.entries(filters || {});
        if (!entries.length) {
            return true;
        }
        return entries.every(([key, constraint]) => this.matchesLocalConstraint((row as Record<string, any>)[key], constraint));
    }

    private matchesLocalConstraint(rowValue: any, constraint: Primitive | Primitive[] | null | undefined): boolean {
        if (constraint === undefined || constraint === null || constraint === '' || (Array.isArray(constraint) && !constraint.length)) {
            return true;
        }
        if (Array.isArray(constraint)) {
            return constraint.some((value) => this.matchesLocalConstraint(rowValue, value));
        }
        if (rowValue === undefined || rowValue === null) {
            return false;
        }
        if (typeof rowValue === 'boolean') {
            return String(rowValue) === String(constraint);
        }
        if (Array.isArray(rowValue)) {
            const haystack = rowValue.map((value) => this.toComparableString(value)).join(' ');
            return haystack.includes(this.toComparableString(constraint));
        }
        return this.toComparableString(rowValue).includes(this.toComparableString(constraint));
    }

    private toComparableString(value: any): string {
        return String(value ?? '').trim().toLowerCase();
    }

    private resolvePage(queryUrl: string, pageIndex: number): Observable<TRow[]> {
        const cached = this.pageCache.get(pageIndex);
        if (cached) {
            console.log(`Serving page ${pageIndex} from cache.`);
            // Ensure displayed total reflects cached pages/next-links
            try {
                this.updateDisplayedTotalAfterPageChange();
            } catch (e) {
                // swallow — update is best-effort
            }
            return of(cached);
        }

        if (this.cacheLocked) {
            return throwError(() => new Error(`Cache limit reached (${this.maxCacheLimit}). Refine your filters to load additional pages.`));
        }

        const requestUrl = pageIndex === 0 ? queryUrl : this.pageToNextLink.get(pageIndex - 1);
        if (!requestUrl) {
            return throwError(() => new Error('Requested page is not available. Try moving sequentially.'));
        }

        return this.fetchBundle(requestUrl, pageIndex).pipe(
            map((bundle) => this.handleBundle(bundle, pageIndex))
        );
    }

    private fetchBundle(url: string, pageIndex: number): Observable<Bundle<TResource>> {
        const requestUrl = this.toRequestUrl(url);
        return this.http.get<Bundle<TResource>>(requestUrl, this.config?.httpOptions).pipe(
            tap((bundle) => this.captureNextLink(bundle, pageIndex))
        );
    }

    private handleBundle(bundle: Bundle<TResource>, pageIndex: number): TRow[] {
        const transformed = this.transformResources(bundle.entry ?? []);
        this.storePage(pageIndex, transformed);
        return transformed;
    }

    private captureNextLink(bundle: Bundle<TResource>, pageIndex: number): void {
        let nextLink = bundle.link?.find((link) => link.relation === 'next')?.url;
        if (nextLink) {
            nextLink = this.normalizeNextLink(nextLink);
            this.pageToNextLink.set(pageIndex, nextLink);
            // We now have knowledge that a next page exists for this page
            this.updateDisplayedTotalAfterPageChange();
        } else {
            this.pageToNextLink.delete(pageIndex);
            this.updateDisplayedTotalAfterPageChange();
        }
    }

    private normalizeNextLink(nextLink: string): string {
        try {
            const backend = (this.backendBase ?? '').toString().replace(/\/$/, '');
            const upstream = (this.upstreamBase ?? '').toString().replace(/\/$/, '');
            if (!backend || !nextLink) {
                return nextLink;
            }

            if (nextLink.startsWith(backend)) {
                return nextLink;
            }

            if (upstream && nextLink.startsWith(upstream)) {
                return backend + nextLink.substring(upstream.length);
            }

            const parsedNext = new URL(nextLink);
            const parsedBackend = new URL(backend);
            const parsedUpstream = upstream ? new URL(upstream) : null;
            const sameBackendHost = parsedNext.host === parsedBackend.host;
            const sameUpstreamHost = !!parsedUpstream && parsedNext.host === parsedUpstream.host;

            if (sameBackendHost || sameUpstreamHost) {
                const backendPath = parsedBackend.pathname.replace(/\/$/, '');
                const nextPath = parsedNext.pathname.replace(/^\//, '');
                const backendPathAlreadyPresent = parsedNext.pathname.startsWith(`${backendPath}/`);
                const normalizedPath = backendPathAlreadyPresent
                    ? parsedNext.pathname
                    : `${backendPath}/${nextPath}`.replace(/\/+/g, '/');
                return `${parsedBackend.origin}${normalizedPath}${parsedNext.search}`;
            }
        } catch {
            // Keep the server-provided link if it cannot be parsed.
        }

        return nextLink;
    }

    private transformResources(entries: BundleEntry<TResource>[]): TRow[] {
        const mapper = this.config?.transformEntry ?? this.defaultTransform;
        return entries
            .map((entry, index) => ({ entry, resource: entry.resource ?? null, index }))
            .filter((wrapper): wrapper is { entry: BundleEntry<TResource>; resource: TResource; index: number } => !!wrapper.resource)
            .map(({ resource, entry, index }) => mapper(resource, entry) || this.defaultTransform(resource, entry, index));
    }

    private defaultTransform(resource: TResource, _entry?: BundleEntry<TResource>, index?: number): TRow {
        const fallbackId = `${resource.resourceType || this.config?.resourceType || 'resource'}-${resource.id || index || 0}`;
        const fallbackRow: TableRowModel = {
            id: resource.id ?? fallbackId,
            resourceType: resource.resourceType ?? this.config?.resourceType ?? 'Resource',
            display: (resource as any)?.text?.div ?? resource.id ?? '—',
            rawResource: resource
        };

        return fallbackRow as unknown as TRow;
    }

    private storePage(pageIndex: number, rows: TRow[]): void {
        this.pageCache.set(pageIndex, rows);
        this.cachedRowTotal += rows.length;
        if (this.cachedRowTotal >= this.maxCacheLimit) {
            this.cacheLocked = true;
        }
        // Update progressive displayed total when a page is stored
        this.updateDisplayedTotalAfterPageChange();
    }

    private resetCache(): void {
        this.pageCache.clear();
        this.pageToNextLink.clear();
        this.cachedRowTotal = 0;
        this.cacheLocked = false;
        // Reset displayed total to a single page worth (will be updated as pages arrive)
        this.displayedTotalSubject.next(this.pageSize);
    }

    private invalidateCachedPagesFrom(pageIndex: number): void {
        let removed = 0;
        for (const [key, rows] of Array.from(this.pageCache.entries())) {
            if (key >= pageIndex) {
                removed += rows.length;
                this.pageCache.delete(key);
            }
        }
        for (const key of Array.from(this.pageToNextLink.keys())) {
            if (key >= pageIndex) {
                this.pageToNextLink.delete(key);
            }
        }
        this.cachedRowTotal = Math.max(0, this.cachedRowTotal - removed);
        this.cacheLocked = this.cachedRowTotal >= this.maxCacheLimit;
    }

    private fetchTotalCount(queryUrl: string): void {
        if (this.disableCountQuery) {
            this.unknownTotalSubject.next(true);
            this.totalSubject.next(null);
            return;
        }

        const summaryUrl = this.buildSummaryUrl(queryUrl);
        const requestUrl = this.toRequestUrl(summaryUrl);

        this.http.get<Bundle<TResource>>(requestUrl, this.config?.httpOptions)
            .pipe(
                map((bundle) => bundle.total ?? null),
                catchError(() => of(null))
            )
            .subscribe((total) => {
                if (typeof total === 'number' && total > 0) {
                    this.unknownTotalSubject.next(false);
                    this.totalSubject.next(total);
                    this.displayedTotalSubject.next(total);
                } else {
                    this.unknownTotalSubject.next(true);
                    this.totalSubject.next(null);
                    // If server did not provide a count, ensure displayed total is at least one page
                    // this.displayedTotalSubject.next(Math.max(this.pageSize, this.cachedRowTotal || this.pageSize));
                }
            });
    }

    private updateDisplayedTotalAfterPageChange(): void {
        console.log('Updating displayed total after page change. CachedRowTotal:', this.cachedRowTotal, 'PageCacheKeys:', Array.from(this.pageCache.keys()), 'PageToNextLinkKeys:', Array.from(this.pageToNextLink.keys()));
        const knownTotal = this.totalSubject.getValue();
        if (typeof knownTotal === 'number') {
            this.displayedTotalSubject.next(knownTotal);
            return;
        }

        // Server total unknown: determine whether there's a next page beyond the cached pages
        const cachedCount = this.cachedRowTotal;
        // Find highest cached page index
        const keys = Array.from(this.pageCache.keys());
        const maxCachedPage = keys.length ? Math.max(...keys) : -1;
        const hasNextForLastCached = this.pageToNextLink.has(maxCachedPage);

        if (hasNextForLastCached) {
            // If server indicates a next page, grow the displayed total by another page size
            const candidate = Math.max(this.displayedTotalSubject.getValue(), cachedCount + this.pageSize);
            this.displayedTotalSubject.next(candidate);
        } else {
            // No further pages — reflect the actual amount fetched so far
            this.displayedTotalSubject.next(cachedCount);
        }
    }

    private buildQueryUrl(baseUrl: string, filters?: TableFilters): string {
        const { path, params } = this.parseUrl(baseUrl);
        params.set('_count', String(this.pageSize));
        params.set('_sort', '-_lastUpdated');
        // params.set('_total', 'accurate');

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                params.delete(key);
                if (value === undefined || value === null || value === '') {
                    return;
                }
                if (Array.isArray(value)) {
                    value.map((v) => this.normalizeFilterValue(v)).filter(Boolean).forEach((v) => params.append(key, v));
                } else {
                    const normalized = this.normalizeFilterValue(value);
                    if (normalized) {
                        params.set(key, normalized);
                    }
                }
            });
        }

        return this.combineUrl(path, params);
    }

    private buildSummaryUrl(baseUrl: string): string {
        const { path, params } = this.parseUrl(baseUrl);
        params.delete('_count');
        params.delete('_sort');
        params.set('_summary', 'count');
        return this.combineUrl(path, params);
    }

    private parseUrl(url: string): { path: string; params: URLSearchParams } {
        if (!url) {
            return { path: '', params: new URLSearchParams() };
        }

        const trimmed = url.trim();
        const { path, query } = this.splitPathAndQuery(trimmed);
        const normalizedQuery = this.normalizeLooseQueryString(query);
        return { path, params: new URLSearchParams(normalizedQuery) };
    }

    private splitPathAndQuery(url: string): { path: string; query: string } {
        const questionMarkIndex = url.indexOf('?');
        if (questionMarkIndex >= 0) {
            return {
                path: url.substring(0, questionMarkIndex),
                query: url.substring(questionMarkIndex + 1)
            };
        }

        const ampIndex = url.indexOf('&');
        if (ampIndex >= 0) {
            return {
                path: url.substring(0, ampIndex),
                query: url.substring(ampIndex + 1)
            };
        }

        return { path: url, query: '' };
    }

    private normalizeLooseQueryString(query: string): string {
        if (!query) {
            return '';
        }

        const cleaned = query
            .replace(/\?(?=[^&=?]+(?:=|$))/g, '&')
            .replace(/^&+/, '')
            .replace(/&+$/, '');

        return cleaned;
    }

    private combineUrl(path: string, params: URLSearchParams): string {
        const queryString = params.toString();
        return queryString ? `${path}?${queryString}` : path;
    }

    private normalizeFilterValue(value: Primitive): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        return String(value).trim();
    }

    private toRequestUrl(url: string): string {
        if (/^https?:\/\//i.test(url)) {
            return url;
        }
        if (this.config?.baseUrl) {
            return `${this.trimTrailingSlash(this.config.baseUrl)}${url.startsWith('/') ? url : `/${url}`}`;
        }
        return url;
    }

    private trimTrailingSlash(value: string): string {
        return value.endsWith('/') ? value.slice(0, -1) : value;
    }

    private hasActiveFilters(filters: TableFilters): boolean {
        return Object.values(filters).some((value) => {
            if (Array.isArray(value)) {
                return value.some((v) => v !== undefined && v !== null && String(v).trim() !== '');
            }
            return value !== undefined && value !== null && String(value).trim() !== '';
        });
    }

    private ensureInitialized(): void {
        if (!this.hasInit) {
            throw new Error('TableFilteringService not initialized. Call init() first.');
        }
    }
}
